import { Banner, Button, Icon, Loader, ProgressSteps } from '@pwc/pwc-deals-ui';
import { Constants } from '@pwc/pwc-deals-web-common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ActiveContentHeader from '../../../../common/components/ContentHeaders/ActiveContentHeader';
import ContentTable from '../../../../common/components/ContentTable/ContentTable';
import { FormLayout } from '../../../../common/components/layouts';
import PublishButton from '../../../../common/components/PublishButton';
import Separator from '../../../../common/components/Separator';
import IngestLibraryItemModal from '../../../contentLibrary/IngestLibraryItemModal/IngestLibraryItemModal';
import { getLibraryItems } from '../../../contentLibrary/libraryItemService';
import { useSections } from '../FreeformAnalysisForm/FreeformAnalysisFormContext';
import {
  setAddLibraryLocation,
  setCurrentSections,
} from '../FreeformAnalysisForm/FreeformAnalysisFormContextUtils';
import getColumnConfig from './columnConfig';
import EmptyLibraryItemsListCard from './EmptyLibraryItemsListCard';
import getSectionsFromSelectedItems from './getSectionsFromSelectedItems';
import getSelectedItemIds from './getSelectedItemIds';
import './LibraryItemListContainer.scss';

const steps = [
  { title: 'first', description: 'Select data' },
  { title: 'second', description: 'Edit layout' },
];

const LibraryItemListContainer = ({ title, returnUrl, operationType }) => {
  const [{ currentSections, nonLaidOutLibraryItems, locationToAdd }, dispatch] =
    useSections();
  const [isLoading, setIsLoading] = useState(true);
  const [allItems, setAllItems] = useState([]);
  const [pbiItems, setPbiItems] = useState([]);
  // const [sortColumnName, setSortColumnName] = useState('name');
  const [sortColumnDate, setSortColumnDate] = useState('dateCreated');
  const [sortColumnDirection, setSortColumnDirection] = useState('desc');
  const [selectedLibraryItemIds, setSelectedLibraryItemIds] = useState(
    getSelectedItemIds(nonLaidOutLibraryItems, currentSections)
  );
  const [isPowerpointIngestionSuccessful, setIsPowerpointIngestionSuccessful] =
    useState(false);
  const { projectId } = useParams();
  const history = useHistory();
  const [showStartIngestionModal, setShowStartIngestionModal] = useState(false);

  const refreshList = useCallback(
    async function refreshList() {
      const fetchedLibraryItems = await getLibraryItems(projectId);
      const filteredLibraryItems = fetchedLibraryItems.filter((li) => {
        return (
          li.type !== Constants.LIBRARY_ITEM_TYPES.RICH_TEXT.value &&
          li.type !== Constants.LIBRARY_ITEM_TYPES.UNDEFINED.value &&
          li.status !== 'DraftCopied'
        );
      });
      const ingestedPbiItems = fetchedLibraryItems.filter(
        (li) => li.type === Constants.LIBRARY_ITEM_TYPES.POWER_BI_REPORT.value
      );
      setPbiItems(ingestedPbiItems);
      setAllItems(filteredLibraryItems);
      setIsLoading(false);
    },
    [projectId]
  );

  useEffect(() => {
    setIsLoading(true);
    refreshList();
  }, [refreshList]);

  const onDeleteLibraryItem = useCallback(async () => {
    setIsLoading(true);
    await refreshList();
  }, [refreshList]);

  const columns = useMemo(
    () =>
      getColumnConfig(
        setAllItems,
        setSortColumnDate,
        setSortColumnDirection,
        setSelectedLibraryItemIds,
        onDeleteLibraryItem
      ),
    [
      setAllItems,
      setSortColumnDate,
      setSortColumnDirection,
      setSelectedLibraryItemIds,
      onDeleteLibraryItem,
    ]
  );

  const returnToOverview = () => {
    history.replace(returnUrl);
  };

  const goToLayout = () => {
    history.replace(`${returnUrl}`);
  };

  function handleSubmit(event) {
    event.preventDefault();
    const { sections, nonLaidOutLibraryItems: nonLaidOutLibraryItems2 } =
      getSectionsFromSelectedItems(
        allItems,
        selectedLibraryItemIds,
        currentSections
      );
    setCurrentSections(sections, dispatch);
    setAddLibraryLocation(
      {
        ...locationToAdd,
        onSubmit: true,
        item: nonLaidOutLibraryItems2[0],
      },
      dispatch
    );
    goToLayout();
  }

  return (
    <form
      data-testid="library-item-list-container-form"
      className="library-item-list-container"
      onSubmit={handleSubmit}
    >
      <FormLayout
        headerComponent={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <ActiveContentHeader>
            <Button
              className="button-link button-theme mx-2 mr-4"
              onClick={returnToOverview}
              data-testid="cancel-button-top"
              type="button"
            >
              {`Back to ${operationType} analysis`}
            </Button>
            <PublishButton
              type="submit"
              isSaving={false}
              isValid
              buttonSize="button-small"
              buttonPosition="top"
              onSaveText="Adding data to analysis..."
              standbyText="Add"
              data-testid="publish-button-top"
            />
          </ActiveContentHeader>
        }
      >
        <h1>{title}</h1>
        <div className="step-indicator-container">
          <ProgressSteps current={1} steps={steps} />
        </div>
        {allItems.length > 0 && (
          <>
            <p className="library-item-description twothirds">
              Choose pre-ingested data or ingest your own using the Excel
              add-in. You can also import data from Power BI and PowerPoint.
            </p>
            <Button
              className="button button-no-underline button-theme mb-4 mt-3"
              data-testid="ingest-power-bi-button"
              type="button"
              onClick={() => {
                setIsPowerpointIngestionSuccessful(false);
                setShowStartIngestionModal(true);
              }}
            >
              <Icon
                name="upload"
                width="24"
                className="fill icon-orange mr-2"
              />
              Import data
            </Button>
          </>
        )}
        {showStartIngestionModal && (
          <IngestLibraryItemModal
            show={showStartIngestionModal}
            handleCancel={() => setShowStartIngestionModal(false)}
            handleSuccessfulIngestion={async () => {
              setIsPowerpointIngestionSuccessful(true);
              setShowStartIngestionModal(false);
              await refreshList();
            }}
            pbiItems={pbiItems}
            projectId={projectId}
            ingestedPowerPointGroups={allItems
              .filter(
                (li) =>
                  li.type ===
                  Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value
              )
              .map((fli) => {
                const libraryItemJson = JSON.parse(fli.data);
                return {
                  driveItemId: libraryItemJson.DriveItemId,
                  slideNumber: libraryItemJson.SlideNumber,
                  groupName: libraryItemJson.GroupName,
                };
              })}
          />
        )}
        {isPowerpointIngestionSuccessful && (
          <Banner
            notice="success"
            text="Data imported successfully."
            icon="checkmark"
            data-testid="ingest-success-message"
          />
        )}

        {isLoading ? (
          <Loader dataTestId="library-item-loading-indicator" />
        ) : (
          <>
            <div data-testid="library-item-list-container">
              {allItems.length === 0 && (
                <EmptyLibraryItemsListCard>
                  <Button
                    className="ui-button button-block import-data"
                    data-testid="ingest-power-bi-button"
                    type="button"
                    onClick={() => {
                      setIsPowerpointIngestionSuccessful(false);
                      setShowStartIngestionModal(true);
                    }}
                  >
                    <Icon
                      name="upload"
                      width="24"
                      className="fill icon-white mr-2"
                    />
                    Import data
                  </Button>
                </EmptyLibraryItemsListCard>
              )}
              {allItems.length > 0 && (
                <ContentTable
                  data={allItems}
                  columns={columns}
                  initialSortColumn={sortColumnDate}
                  initialSortDirection={sortColumnDirection}
                  type="library-item"
                  rowClassFunction={(libraryItem) => {
                    return libraryItem.selected ? 'row-selected' : '';
                  }}
                />
              )}
            </div>
          </>
        )}

        <Separator />
        <div className="form-bottom-actions">
          <Button
            className="button-link form-cancel-button"
            type="button"
            onClick={returnToOverview}
            data-testid="cancel-button-bottom"
          >
            {`Back to ${operationType} analysis`}
          </Button>
          <PublishButton
            isSaving={false}
            isValid
            buttonPosition="bottom"
            onSaveText="Adding data to analysis..."
            standbyText="Add and continue"
          />
        </div>
      </FormLayout>
    </form>
  );
};

export default LibraryItemListContainer;
